import { useCallback, useEffect, useState } from 'react';
import { Plus, ArrowLeft, Trash2 } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { Skeleton } from '../Skeleton';
import {
    listMentorshipProjects,
    createMentorshipProject,
    archiveMentorshipProject,
    listRisks,
    createRisk,
    archiveRisk,
    type MentorshipProject,
    type Risk,
    type Priority,
} from '../../mentorshipService';
const PRIORITIES: Priority[] = ['low', 'medium', 'high'];

function ProjectDetail({
    project,
    onBack,
    onArchived,
}: {
    project: MentorshipProject;
    onBack: () => void;
    onArchived: () => void;
}) {
    const [risks, setRisks] = useState<Risk[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [trigger, setTrigger] = useState('');
    const [impact, setImpact] = useState('');
    const [mitigation, setMitigation] = useState('');
    const [priority, setPriority] = useState<Priority>('medium');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const page = await listRisks(project.id);
            setRisks(page.results);
        } catch {
            setRisks([]);
        } finally {
            setLoading(false);
        }
    }, [project.id]);

    useEffect(() => {
        void load();
    }, [load]);

    const addRisk = async () => {
        if (!trigger.trim() || !impact.trim() || !mitigation.trim()) return;
        setSaving(true);
        try {
            const r = await createRisk({
                project_id: project.id,
                trigger: trigger.trim(),
                impact: impact.trim(),
                mitigation: mitigation.trim(),
                priority,
            });
            setRisks((prev) => [r, ...(prev ?? [])]);
            setTrigger('');
            setImpact('');
            setMitigation('');
            setPriority('medium');
        } catch (err) {
            console.error('[ProjectDetail] addRisk failed', err);
        } finally {
            setSaving(false);
        }
    };

    const removeRisk = async (id: string) => {
        try {
            await archiveRisk(id);
            setRisks((prev) => (prev ?? []).filter((r) => r.id !== id));
        } catch (err) {
            console.error('[ProjectDetail] removeRisk failed', err);
        }
    };

    const archive = async () => {
        try {
            await archiveMentorshipProject(project.id);
            onArchived();
        } catch (err) {
            console.error('[ProjectDetail] archive failed', err);
        }
    };

    return (
        <div className="project-detail">
            <button
                type="button"
                className="mentorship-stack__back"
                onClick={onBack}
                aria-label="Back to projects"
            >
                <ArrowLeft size={18} /> Back
            </button>

            <Card variant="stacked">
                <Card.Header>
                    <h3>{project.name}</h3>
                </Card.Header>
                <Card.Body>
                    {project.client_id && <p>Client: {project.client_id}</p>}
                    {project.description && <p>{project.description}</p>}
                    {project.is_active ? (
                        <Button variant="ghost" size="sm" onClick={archive}>
                            Archive project
                        </Button>
                    ) : (
                        <p>Archived</p>
                    )}
                </Card.Body>
            </Card>

            <Card variant="stacked">
                <Card.Header>
                    <h3>Add risk</h3>
                </Card.Header>
                <Card.Body>
                    <input
                        type="text"
                        placeholder="Trigger…"
                        value={trigger}
                        onChange={(e) => setTrigger(e.target.value)}
                    />
                    <input
                        type="text"
                        placeholder="Impact…"
                        value={impact}
                        onChange={(e) => setImpact(e.target.value)}
                    />
                    <input
                        type="text"
                        placeholder="Mitigation…"
                        value={mitigation}
                        onChange={(e) => setMitigation(e.target.value)}
                    />
                    <select
                        aria-label="Risk priority"
                        value={priority}
                        onChange={(e) => setPriority(e.target.value as Priority)}
                    >
                        {PRIORITIES.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={addRisk}
                        disabled={saving}
                    >
                        {saving ? 'Adding…' : 'Add risk'}
                    </Button>
                </Card.Body>
            </Card>

            <h3>Risks</h3>
            {loading ? (
                <Skeleton width="100%" height="80px" borderRadius="12px" />
            ) : !risks || risks.length === 0 ? (
                <p>No risks yet.</p>
            ) : (
                <ul className="project-detail__risks">
                    {risks.map((r) => (
                        <li key={r.id}>
                            <Card variant="row">
                                <Card.Header>
                                    <div>
                                        <p><strong>{r.trigger}</strong></p>
                                        <p>Impact: {r.impact}</p>
                                        <p>Mitigation: {r.mitigation}</p>
                                        <span className="mentorship-chip mentorship-chip--module">
                                            {r.priority}
                                        </span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        icon={<Trash2 size={14} />}
                                        onClick={() => removeRisk(r.id)}
                                    >
                                        Archive
                                    </Button>
                                </Card.Header>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function NewProjectModal({
    onClose,
    onCreated,
}: {
    onClose: () => void;
    onCreated: (p: MentorshipProject) => void;
}) {
    const [name, setName] = useState('');
    const [menteeId, setMenteeId] = useState('');
    const [clientId, setClientId] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        if (!name.trim() || !menteeId.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const p = await createMentorshipProject({
                name: name.trim(),
                mentee_id: Number(menteeId),
                client_id: clientId.trim() || undefined,
                description: description.trim() || undefined,
            });
            onCreated(p);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Create failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} size="md" ariaLabel="New project">
            <Modal.Header onClose={onClose}>Start new project</Modal.Header>
            <Modal.Body>
                <label htmlFor="np-name">Project name</label>
                <input
                    id="np-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
                <label htmlFor="np-mentee">Mentee user id</label>
                <input
                    id="np-mentee"
                    type="number"
                    value={menteeId}
                    onChange={(e) => setMenteeId(e.target.value)}
                />
                <label htmlFor="np-client">Client id (optional)</label>
                <input
                    id="np-client"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                />
                <label htmlFor="np-desc">Description (optional)</label>
                <textarea
                    id="np-desc"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
                {error && <p className="report-submit__error">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
                <Button variant="primary" size="md" onClick={submit} disabled={saving}>
                    {saving ? 'Creating…' : 'Create'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function MentorProjectsList() {
    const [projects, setProjects] = useState<MentorshipProject[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState<MentorshipProject | null>(null);
    const [showNew, setShowNew] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const page = await listMentorshipProjects({ active: true });
            setProjects(page.results);
        } catch {
            setProjects([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <div className="mentorship-stack" data-mode={active ? 'detail' : 'list'}>
            <div className="mentorship-stack__list">
                <div className="mentor-projects__header">
                    <h3>Your projects</h3>
                    <Button
                        variant="primary"
                        size="md"
                        icon={<Plus size={16} />}
                        onClick={() => setShowNew(true)}
                    >
                        New project
                    </Button>
                </div>
                {loading ? (
                    <Skeleton width="100%" height="80px" borderRadius="12px" />
                ) : !projects || projects.length === 0 ? (
                    <div className="empty-state">
                        <h3>No active projects</h3>
                        <p>Start a new engagement to begin curating risks.</p>
                    </div>
                ) : (
                    <ul className="mentor-projects__list">
                        {projects.map((p) => (
                            <li key={p.id}>
                                <Card
                                    variant="row"
                                    interactive
                                    className={active?.id === p.id ? 'is-active' : ''}
                                    onClick={() => setActive(p)}
                                >
                                    <Card.Header>
                                        <div>
                                            <p><strong>{p.name}</strong></p>
                                            {p.client_id && <p>Client: {p.client_id}</p>}
                                            <p>{p.risks_count} risks</p>
                                        </div>
                                    </Card.Header>
                                </Card>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className="mentorship-stack__detail">
                {active ? (
                    <ProjectDetail
                        key={active.id}
                        project={active}
                        onBack={() => setActive(null)}
                        onArchived={() => {
                            setActive(null);
                            void load();
                        }}
                    />
                ) : (
                    <div className="empty-state">
                        <h3>Select a project</h3>
                        <p>Pick a project from the list to view risks.</p>
                    </div>
                )}
            </div>
            {showNew && (
                <NewProjectModal
                    onClose={() => setShowNew(false)}
                    onCreated={(p) => {
                        setShowNew(false);
                        setProjects((prev) => [p, ...(prev ?? [])]);
                        setActive(p);
                    }}
                />
            )}
        </div>
    );
}

export default MentorProjectsList;
